/**
 * HTML Report Generator
 *
 * Generates a self-contained HTML report from a TestRunReport.
 * Utility functions for HTML escaping, ANSI stripping, and formatting.
 */

import { writeFileSync, renameSync, mkdirSync, existsSync, readFileSync } from 'node:fs';
import { dirname, join, resolve } from 'node:path';
import { spawn } from 'node:child_process';
import type { TestRunReport, ArtifactRunManifest } from '@testrelic/core';
import type { ResolvedConfig } from './config.js';
import type { PresentedCounts } from './expected-status.js';
import { renderHtmlDocument, renderStreamingHtmlDocument } from './html-template.js';
import { openInBrowser } from './browser-open.js';
import { startArtifactServer } from './artifact-server.js';
import { startReportServer } from './report-server.js';
import type { ReportServerHandle } from './report-server.js';

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(str: string): string {
  return str.replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch] ?? ch);
}

// SAFETY: Regex targets all ANSI SGR escape sequences (color/style codes).
// The ESC control character (U+001B) is the literal thing being stripped, so
// no-control-regex cannot be satisfied without changing what this matches --
// the rule guards against control characters reaching a pattern by accident,
// and here that character is the whole point. Disabled on this line only.
// eslint-disable-next-line no-control-regex
const ANSI_REGEX = /\u001b\[[0-9;]*m/g;

export function stripAnsi(str: string): string {
  return str.replace(ANSI_REGEX, '');
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// HTML Report Generation
// ---------------------------------------------------------------------------

/**
 * `presented` rides beside `summary` as `presentedSummary`, in the HTML only:
 * report.json is written from the untouched report by writeReport, and keeps
 * counting by actual status. The verdict band reads the presented counts so a
 * `test.fail()` that failed as expected is not headlined as a failure.
 */
export function generateHtmlReport(
  report: TestRunReport,
  serverPort?: number | null,
  manifest?: ArtifactRunManifest | null,
  presented?: PresentedCounts | null,
): string {
  const reportJson = JSON.stringify(presented ? { ...report, presentedSummary: presented } : report);
  const manifestJson = manifest ? JSON.stringify(manifest) : null;
  return renderHtmlDocument(reportJson, serverPort, manifestJson);
}

export async function writeHtmlReport(
  report: TestRunReport,
  config: ResolvedConfig,
  manifest?: ArtifactRunManifest | null,
  presented?: PresentedCounts | null,
): Promise<void> {
  try {
    let serverPort: number | null = null;
    let serverHandle: ReportServerHandle | null = null;
    const isStreamingMode = config.reportMode === 'streaming' || (
      config.reportMode === 'auto' && report.timeline.length === 0 && report.summary.total >= config.streamingThreshold
    );

    // Step 1: Write HTML first (server port not needed — JS auto-detects via window.location)
    let html: string;
    const outputPath = config.htmlReportPath;
    const htmlDir = dirname(outputPath);
    mkdirSync(htmlDir, { recursive: true });

    let summaryJson = '';
    let indexJson = '[]';
    let manifestJson: string | null = null;
    const presentedJson = presented ? JSON.stringify(presented) : null;

    if (isStreamingMode) {
      // The streaming HTML template reads the run-header fields (run id, dates,
      // duration, ci, metadata, shard ids) off the summary object it receives.
      // report.summary only carries the pass/fail counts, so enrich it with the
      // report's top-level run metadata — otherwise the header renders
      // "Run —", "Invalid Date", and "Duration 0ms".
      summaryJson = JSON.stringify({
        ...report.summary,
        testRunId: report.testRunId,
        startedAt: report.startedAt,
        completedAt: report.completedAt,
        totalDuration: report.totalDuration,
        ci: report.ci,
        metadata: report.metadata,
        shardRunIds: report.shardRunIds,
      });
      const outputDir = dirname(config.outputPath);
      const reportDir = join(outputDir, '.testrelic-report');
      try {
        // Prefer pre-built compact index (avoids expensive parse-reserialize)
        const compactPath = join(reportDir, 'index-compact.json');
        const fullPath = join(reportDir, 'index.json');
        if (existsSync(compactPath)) {
          indexJson = readFileSync(compactPath, 'utf-8');
        } else if (existsSync(fullPath)) {
          indexJson = readFileSync(fullPath, 'utf-8');
        }
      } catch { /* use empty */ }
      manifestJson = manifest ? JSON.stringify(manifest) : null;
      html = renderStreamingHtmlDocument(summaryJson, indexJson, null, manifestJson, presentedJson);
    } else {
      html = generateHtmlReport(report, null, manifest, presented);
    }

    const tmpPath = outputPath + '.tmp';
    writeFileSync(tmpPath, html, 'utf-8');
    renameSync(tmpPath, outputPath);

    // Step 2: Start server
    if (isStreamingMode) {
      // Streaming mode: start a report server (needed for test detail fetching)
      if (report.ci === null) {
        const outputDir = dirname(config.outputPath);
        const reportDir = join(outputDir, '.testrelic-report');
        const htmlPath = resolve(outputPath);
        try {
          // Shut down any stale servers first to avoid port conflicts
          await shutdownExistingServers();

          // Try spawning a detached server (survives after Playwright exits)
          serverPort = await spawnDetachedServer(reportDir);

          // Fallback: start in-process server if detached spawn failed
          if (!serverPort) {
            serverHandle = await startReportServer(reportDir, { htmlPath });
            serverPort = serverHandle.port;
            // Keep Node alive so the server doesn't die with Playwright
            const keepAlive = setInterval(() => {}, 60_000);
            const cleanup = (): void => {
              clearInterval(keepAlive);
              serverHandle?.dispose();
            };
            process.on('SIGINT', cleanup);
            process.on('SIGTERM', cleanup);
            // Auto-shutdown after 30 minutes
            setTimeout(cleanup, 30 * 60 * 1000).unref();
          }

          if (serverPort) {
            process.stderr.write(
              `\n  Report server: http://127.0.0.1:${serverPort}\n`,
            );
            // Rewrite HTML with embedded server port so file:// opens can still connect
            try {
              const updatedHtml = renderStreamingHtmlDocument(summaryJson, indexJson, serverPort, manifestJson, presentedJson);
              const tmpRewrite = outputPath + '.tmp';
              writeFileSync(tmpRewrite, updatedHtml, 'utf-8');
              renameSync(tmpRewrite, outputPath);
            } catch { /* non-fatal */ }
          }
        } catch {
          // Server start failure is non-fatal
        }
      }
    } else {
      // Embedded mode: start an artifact server if applicable.
      //
      // NOTE: nothing in the page talks to this server. The HTML is rendered
      // above with serverPort null and never re-rendered (streaming rewrites
      // itself once the port is known; this branch does not), so the
      // `artifact-server-port` meta tag is never emitted — and no client
      // module reads that tag or rewrites an artifact URL in any case.
      // Artifacts load as relative file:// paths, which is why the CSP has to
      // permit the file: scheme. Left running because a user can still fetch
      // artifacts from it by hand; it is a candidate for removal.
      if (config.openReport && report.ci === null && config.includeArtifacts) {
        const outputDir = dirname(config.outputPath);
        const artifactsDir = join(outputDir, 'artifacts');
        if (existsSync(artifactsDir)) {
          try {
            const server = await startArtifactServer(artifactsDir);
            serverPort = server.port;
            process.on('exit', () => { server.dispose(); });
          } catch {
            // Server start failure is non-fatal
          }
        }
      }
    }

    // Step 3: Auto-open in browser if configured and not in CI
    if (config.openReport && report.ci === null) {
      if (isStreamingMode && serverPort) {
        openInBrowser(`http://127.0.0.1:${serverPort}`);
      } else {
        const absolutePath = resolve(outputPath);
        openInBrowser(absolutePath);
      }
    }
  } catch (err) {
    // FR-026: log to stderr, never crash
    process.stderr.write(
      `[testrelic] Failed to write HTML report: ${err instanceof Error ? err.message : String(err)}\n`,
    );
  }
}

// ---------------------------------------------------------------------------
// Detached Server Helpers
// ---------------------------------------------------------------------------

const DEFAULT_PORT = 9323;
const MAX_PORT_ATTEMPTS = 10;

/** Check if a report server is already running on the default port range. */
async function detectExistingServer(): Promise<number | null> {
  for (let port = DEFAULT_PORT; port < DEFAULT_PORT + MAX_PORT_ATTEMPTS; port++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 500);
      const resp = await fetch(`http://127.0.0.1:${port}/api/health`, { signal: controller.signal });
      clearTimeout(timer);
      if (resp.ok) return port;
    } catch {
      // Not running on this port
    }
  }
  return null;
}

/** Shut down any existing report servers on the default port range. */
async function shutdownExistingServers(): Promise<void> {
  for (let port = DEFAULT_PORT; port < DEFAULT_PORT + MAX_PORT_ATTEMPTS; port++) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 1000);
      await fetch(`http://127.0.0.1:${port}/api/shutdown`, { method: 'POST', signal: controller.signal });
      clearTimeout(timer);
    } catch {
      // Not running on this port — skip
    }
  }
  // Brief pause to let ports release
  await new Promise(r => setTimeout(r, 300));
}

/** Spawn a detached report server process that survives after Playwright exits. */
async function spawnDetachedServer(reportDir: string): Promise<number | null> {
  // Find the CLI entry point — try multiple locations
  const candidates = [
    join(__dirname, 'cli.cjs'),
    join(__dirname, 'cli.js'),
    join(__dirname, '..', 'dist', 'cli.cjs'),
    join(__dirname, '..', 'dist', 'cli.js'),
  ];
  const cliPath = candidates.find(p => existsSync(p));
  if (!cliPath) {
    // Fallback: run in-process server (will die with Playwright)
    const handle = await startReportServer(reportDir);
    process.on('exit', () => { handle?.dispose(); });
    return handle.port;
  }

  return new Promise<number | null>((resolvePort) => {
    const child = spawn(process.execPath, [cliPath, 'serve', reportDir], {
      detached: true,
      stdio: ['ignore', 'ignore', 'pipe'],
      env: { ...process.env },
    });

    let stderr = '';
    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        child.stderr?.removeAllListeners();
        // Server didn't announce its port in time — try detecting it
        detectExistingServer().then(resolvePort);
      }
    }, 5000);

    child.stderr?.on('data', (chunk: Buffer) => {
      stderr += chunk.toString();
      // Look for "Report server running at: http://127.0.0.1:XXXX"
      const match = stderr.match(/127\.0\.0\.1:(\d+)/);
      if (match && !resolved) {
        resolved = true;
        clearTimeout(timeout);
        child.stderr?.removeAllListeners();
        child.unref();
        resolvePort(Number(match[1]));
      }
    });

    child.on('error', () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolvePort(null);
      }
    });

    child.on('exit', () => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        resolvePort(null);
      }
    });

    // Detach the child so it survives after Playwright exits
    child.unref();
  });
}
